import React, { useState } from "react";
import {
  Typography,
  Paper,
  Box,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogContent,
  IconButton,
  Chip,
  Card,
  CardContent,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LaunchIcon from "@mui/icons-material/Launch";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SchoolIcon from "@mui/icons-material/School";

const IELTSPreparationPlatform = () => {
  const [openImage, setOpenImage] = useState(null);

  // Project data
  const project = {
    title: "IELTS Preparation Platform",
    subtitle: "Liulishuo IELTS",
    overview:
      "An advanced AI-powered IELTS preparation platform designed to provide personalized learning experiences for students preparing for the IELTS exam. The platform leverages cutting-edge AI technology to deliver tailored content, adaptive learning paths, and comprehensive performance analytics.",
    innovations: [
      {
        title: "Personalized Learning System",
        description:
          "The platform delivers dynamically generated lessons tailored to each student's specific needs and skill level. The system continuously analyzes user performance to optimize the learning path, ensuring students focus on areas that will most improve their IELTS scores.",
        icon: <SchoolIcon fontSize="large" />,
      },
      {
        title: "AI-Powered Analytics",
        description:
          "Comprehensive performance tracking with statistics and progress visualization, personalized reports with AI-generated insights on strengths and improvement areas, and algorithmically generated study plans based on user performance.",
        icon: <AnalyticsIcon fontSize="large" />,
      },
      {
        title: "Interactive AI Assistant",
        description:
          "An intelligent AI guide that answers student questions in real-time, provides personalized guidance based on learning patterns, and adapts recommendations as the student progresses through the course materials.",
        icon: <SmartToyIcon fontSize="large" />,
      },
    ],
    contentPipeline: [
      "Analyzes existing company content resources",
      "Studies high-performing IELTS teacher videos",
      "Examines real IELTS questions and response patterns",
      "Identifies unique insights and effective teaching approaches",
      "Generates original content and learning scripts",
    ],
    resourceOptimization: [
      "Latest image generation models to create original visual assets",
      "Video generation technology to produce instructional content",
      "AI-assisted design tools to maintain consistent branding and UX",
    ],
    images: {
      main: [
        {
          src: "/images/ielts/main_page.png",
          title: "IELTS Platform Main Page",
          description:
            "The main dashboard showing course structure and learning path",
        },
        {
          src: "/images/ielts/dashboard.png",
          title: "Student Dashboard",
          description:
            "Personalized analytics and progress tracking for students",
        },
        {
          src: "/images/ielts/lesson.png",
          title: "Interactive Lesson Interface",
          description: "Engaging lesson experience with multimedia content",
        },
        {
          src: "/images/ielts/lesson_result.png",
          title: "Lesson Results & Feedback",
          description: "Detailed performance analysis after completing lessons",
        },
        {
          src: "/images/ielts/content_creation_workflow.png",
          title: "Content Creation Workflow",
          description:
            "The AI-powered pipeline for generating learning materials",
        },
      ],
      ipDemo: [
        {
          src: "/images/ielts/ip_demo/listening_ip.png",
          title: "Listening Practice Module",
          description: "Interactive listening comprehension exercises",
          transparent: true,
        },
        {
          src: "/images/ielts/ip_demo/good_feedback.png",
          title: "Positive Feedback Interface",
          description: "Encouraging feedback for correct responses",
          transparent: true,
        },
        {
          src: "/images/ielts/ip_demo/bad_feedback.png",
          title: "Constructive Feedback Interface",
          description: "Helpful guidance when students make mistakes",
          transparent: true,
        },
        {
          src: "/images/ielts/ip_demo/test_positive.gif",
          title: "Interactive Test Experience",
          description: "Animated feedback creates an engaging test environment",
          isGif: true,
        },
      ],
    },
  };

  // Image modal handlers
  const handleImageClick = (img) => setOpenImage(img);
  const handleClose = () => setOpenImage(null);

  // Component for displaying an image card with description
  const ImageCard = ({ image, title, description, fullWidth = false }) => (
    <Paper elevation={2} sx={{ p: 2, height: "100%" }}>
      <Box sx={{ cursor: "pointer" }} onClick={() => handleImageClick(image)}>
        <img
          src={image.src}
          alt={image.title}
          style={{
            width: "100%",
            height: fullWidth ? "auto" : 220,
            objectFit: fullWidth ? "contain" : "cover",
            borderRadius: "8px",
            background: image.transparent ? "transparent" : undefined,
          }}
        />
      </Box>
      <Typography variant="h6" sx={{ mt: 2 }}>
        {title || image.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {description || image.description}
      </Typography>
    </Paper>
  );

  return (
    <>
      {/* Image Lightbox Dialog */}
      <Dialog open={!!openImage} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogContent sx={{ p: 0, position: "relative", bgcolor: "#111" }}>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "#fff",
              zIndex: 1,
            }}
          >
            <CloseIcon fontSize="large" />
          </IconButton>
          {openImage && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                p: 2,
              }}
            >
              <img
                src={openImage.src}
                alt={openImage.title}
                style={{
                  maxWidth: "100%",
                  maxHeight: "80vh",
                  width: "auto",
                  height: "auto",
                  borderRadius: 8,
                  boxShadow: "0 4px 32px #000a",
                  background: openImage.transparent ? "#000" : "#222",
                }}
              />
              <Typography variant="h6" color="#fff" sx={{ mt: 2 }}>
                {openImage.title}
              </Typography>
              <Typography variant="body2" color="#ccc" sx={{ mt: 1, mb: 1 }}>
                {openImage.description}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Project Header */}
      <Paper
        elevation={3}
        sx={{
          p: 4,
          mb: 4,
          background: "linear-gradient(135deg, #1a237e 0%, #283593 100%)",
          color: "white",
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h4" gutterBottom fontWeight="bold">
              {project.title}
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ mb: 2, opacity: 0.9 }}>
              {project.subtitle}
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem" }}>
              {project.overview}
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Chip
                icon={<CalendarTodayIcon />}
                label="Launched May 2025"
                sx={{ mr: 1, mb: 1, bgcolor: "rgba(255,255,255,0.15)" }}
              />
              <Chip
                label="3-Month Development Cycle"
                sx={{ mr: 1, mb: 1, bgcolor: "rgba(255,255,255,0.15)" }}
              />
              <Chip
                icon={<LaunchIcon />}
                label="Commercial Launch: June 2025"
                sx={{ mr: 1, mb: 1, bgcolor: "rgba(255,255,255,0.15)" }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Box
                sx={{ cursor: "pointer" }}
                onClick={() => handleImageClick(project.images.main[0])}
              >
                <img
                  src={project.images.main[0].src}
                  alt={project.images.main[0].title}
                  style={{
                    width: "100%",
                    borderRadius: "8px",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
                  }}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Key Features */}
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Key Features
        </Typography>
        <Grid container spacing={3}>
          {project.innovations.map((innovation, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box sx={{ mb: 2, color: "primary.main" }}>
                  {innovation.icon}
                </Box>
                <Typography variant="h6" gutterBottom>
                  {innovation.title}
                </Typography>
                <Typography variant="body2">
                  {innovation.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* User Experience */}
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          Learning Experience
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ImageCard image={project.images.main[2]} />
          </Grid>
          <Grid item xs={12} md={6}>
            <ImageCard image={project.images.main[3]} />
          </Grid>
        </Grid>
      </Paper>

      {/* Analytics Dashboard */}
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          Analytics Dashboard
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <ImageCard image={project.images.main[1]} fullWidth={true} />
          </Grid>
        </Grid>
      </Paper>

      {/* Content Creation System */}
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Innovative Content Creation System
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              AI-Powered Content Pipeline
            </Typography>
            <Typography variant="body1" paragraph>
              The platform features an innovative content creation system that
              streamlines the development of high-quality educational materials:
            </Typography>
            <List>
              {project.contentPipeline.map((step, index) => (
                <ListItem key={index} alignItems="flex-start">
                  <ListItemIcon>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "bold",
                      }}
                    >
                      {index + 1}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={step}
                    primaryTypographyProps={{ variant: "body1" }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                cursor: "pointer",
                mb: 2,
              }}
              onClick={() => handleImageClick(project.images.main[4])}
            >
              <img
                src={project.images.main[4].src}
                alt={project.images.main[4].title}
                style={{
                  width: "100%",
                  borderRadius: "8px",
                }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              The AI-powered pipeline for generating learning materials
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* AI-Generated Visual Assets */}
      <Paper elevation={3} sx={{ p: 4, mb: 4, bgcolor: "#000" }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3, color: "white" }}>
          AI-Generated Visual Assets
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={5}>
            <Typography variant="h6" gutterBottom sx={{ color: "white" }}>
              Advanced Image & Video Generation
            </Typography>
            <Typography variant="body1" paragraph sx={{ color: "white" }}>
              To overcome design resource limitations while maintaining the
              aggressive timeline, the platform leverages cutting-edge AI
              technologies:
            </Typography>
            <List>
              {project.resourceOptimization.map((resource, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "#4caf50" }} />
                  </ListItemIcon>
                  <ListItemText primary={resource} sx={{ color: "white" }} />
                </ListItem>
              ))}
            </List>
            <Typography variant="body1" sx={{ mt: 2, color: "white" }}>
              These AI-generated assets maintain consistent quality while
              dramatically reducing production time and resources.
            </Typography>
          </Grid>
          <Grid item xs={12} md={7}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "center",
                  }}
                  onClick={() => handleImageClick(project.images.ipDemo[3])}
                >
                  <img
                    src={project.images.ipDemo[3].src}
                    alt={project.images.ipDemo[3].title}
                    style={{
                      maxWidth: "100%",
                      maxHeight: 200,
                      objectFit: "contain",
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "center",
                  }}
                  onClick={() => handleImageClick(project.images.ipDemo[0])}
                >
                  <img
                    src={project.images.ipDemo[0].src}
                    alt={project.images.ipDemo[0].title}
                    style={{
                      maxWidth: "100%",
                      maxHeight: 120,
                      objectFit: "contain",
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "center",
                  }}
                  onClick={() => handleImageClick(project.images.ipDemo[1])}
                >
                  <img
                    src={project.images.ipDemo[1].src}
                    alt={project.images.ipDemo[1].title}
                    style={{
                      maxWidth: "100%",
                      maxHeight: 120,
                      objectFit: "contain",
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "center",
                  }}
                  onClick={() => handleImageClick(project.images.ipDemo[2])}
                >
                  <img
                    src={project.images.ipDemo[2].src}
                    alt={project.images.ipDemo[2].title}
                    style={{
                      maxWidth: "100%",
                      maxHeight: 120,
                      objectFit: "contain",
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Results */}
      <Paper
        elevation={3}
        sx={{
          p: 4,
          background: "linear-gradient(135deg, #1a237e 0%, #283593 100%)",
          color: "white",
        }}
      >
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          Results & Impact
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                p: 3,
                textAlign: "center",
                bgcolor: "rgba(255,255,255,0.1)",
                borderRadius: 2,
                height: "100%",
              }}
            >
              <Typography variant="h2" fontWeight="bold">
                3
              </Typography>
              <Typography variant="h6">Month Development Cycle</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                vs. industry standard 9+ months
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                p: 3,
                textAlign: "center",
                bgcolor: "rgba(255,255,255,0.1)",
                borderRadius: 2,
                height: "100%",
              }}
            >
              <Typography variant="h2" fontWeight="bold">
                70%
              </Typography>
              <Typography variant="h6">
                Reduced Content Creation Time
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                through AI-powered generation
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                p: 3,
                textAlign: "center",
                bgcolor: "rgba(255,255,255,0.1)",
                borderRadius: 2,
                height: "100%",
              }}
            >
              <Typography variant="h2" fontWeight="bold">
                100%
              </Typography>
              <Typography variant="h6">Personalized Learning</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                adaptive content for each student
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};

export default IELTSPreparationPlatform;
